/**
 * Imported Template Store
 *
 * Session-scoped registry of recipes imported through the Templates gallery
 * "Import from JSON" card. Imported recipes are converted to
 * `TemplateDefinition` via the recipe adapter and merged into the gallery by
 * `useTemplates`, so they preview and apply exactly like built-in templates.
 *
 * Entries live in memory only — they survive SPA navigation (including the
 * create-workflow → designer auto-apply hop) but are gone on page reload.
 * This is intentional: import exists to *test* a generated recipe before it
 * is contributed to the public recipes repo, not to persist it.
 */

import { create } from 'zustand';
import type { Recipe } from '@/types/recipe';
import type { TemplateDefinition } from '@/types/template';
import {
  recipeToTemplateDefinition,
  extractRecipeExtras,
  type RecipeExtras,
} from '@/data/recipes/recipeAdapter';

export interface ImportedTemplateEntry {
  template: TemplateDefinition;
  extras: RecipeExtras;
  recipe: Recipe;
}

interface ImportedTemplateState {
  /** Imported templates keyed by template id (recipe metadata.id). */
  entries: Record<string, ImportedTemplateEntry>;
  /**
   * Register a validated recipe. Re-importing the same id replaces the
   * previous entry. Returns the converted template.
   */
  importRecipe: (recipe: Recipe) => TemplateDefinition;
  removeImportedTemplate: (id: string) => void;
  clearImportedTemplates: () => void;
}

export const useImportedTemplateStore = create<ImportedTemplateState>((set) => ({
  entries: {},
  importRecipe: (recipe) => {
    const template = recipeToTemplateDefinition(recipe);
    const extras = extractRecipeExtras(recipe);
    set((state) => ({
      entries: {
        ...state.entries,
        [template.id]: { template, extras, recipe },
      },
    }));
    return template;
  },
  removeImportedTemplate: (id) =>
    set((state) => {
      const { [id]: _removed, ...rest } = state.entries;
      return { entries: rest };
    }),
  clearImportedTemplates: () => set({ entries: {} }),
}));

/**
 * Extras lookup for imported templates — mirrors `getRecipeExtras` from
 * `@/data/templates` for the imported set. Safe to call from non-React code.
 */
export function getImportedRecipeExtras(templateId: string): RecipeExtras | undefined {
  return useImportedTemplateStore.getState().entries[templateId]?.extras;
}
